import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useNavigate } from "react-router";
import SearchField from "../../interface/shared/searchfield";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { useEffect, useRef, useState } from "react";
import { keyframes } from "@mui/system";
import { FadeIn } from "../../interface/shared/animations";
import { ProviderStockRelocationRequests, useStockRelocationRequests } from "../../api/stockrelocation";
import { useEcho } from "../../shared/echo";

const highlightFade = keyframes`
    0% { background-color: rgba(46, 125, 50, 0.12); }
    100% { background-color: transparent; }
`;

export default function StockRelocations() {
    return (
        <DashboardLayout>
            <ProviderStockRelocationRequests>
                <StockRelocationsContent />
            </ProviderStockRelocationRequests>
        </DashboardLayout>
    );
}

function StockRelocationsContent() {
    const { relocationRequests, errors, loading, fetchRelocationRequests, page, perPage, total, setPage, setPerPage, searchTerm, setSearchTerm, sortField, sortOrder, setSort } = useStockRelocationRequests();
    const navigate = useNavigate();
    const echo = useEcho();
    const fetchRef = useRef(fetchRelocationRequests);
    fetchRef.current = fetchRelocationRequests;
    const prevIdsRef = useRef<Set<number>>(new Set());
    const broadcastTriggered = useRef(false);
    const [highlightedIds, setHighlightedIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (!echo) return;

        const channel = echo.private("warehouse");

        const reload = () => {
            broadcastTriggered.current = true;
            fetchRef.current(true);
        };

        channel.listen(".stock.relocation.created", reload);
        channel.listen(".stock.relocation.deleted", reload);

        return () => {
            channel.stopListening(".stock.relocation.created", reload);
            channel.stopListening(".stock.relocation.deleted", reload);
        };
    }, [echo]);

    useEffect(() => {
        const currentIds = new Set(relocationRequests.map((r) => r.id));

        if (broadcastTriggered.current && prevIdsRef.current.size > 0) {
            const newIds = new Set<number>();
            currentIds.forEach((id) => {
                if (!prevIdsRef.current.has(id)) newIds.add(id);
            });

            if (newIds.size > 0) {
                setHighlightedIds(newIds);
                const timer = setTimeout(() => setHighlightedIds(new Set()), 1500);
                return () => clearTimeout(timer);
            }
        }

        prevIdsRef.current = currentIds;
        broadcastTriggered.current = false;
    }, [relocationRequests]);

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Stock Relocation Requests
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Requests to relocate stock from one location to another.
                    </Typography>
                </Box>
                <SearchField
                    placeholder="Search relocation requests by product name, location or level"
                    value={searchTerm}
                    onChange={setSearchTerm}
                />
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => fetchRelocationRequests()}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Product Code</TableCell>
                                    <TableCell>Product Name</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Level</TableCell>
                                    <TableCell>Requested By</TableCell>
                                    <TableCell>Requested At</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === "product_code"}
                                                direction={sortField === "product_code" ? sortOrder : "asc"}
                                                onClick={() => setSort("product_code")}
                                            >
                                                Product Code
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Product Name</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === "location"}
                                                direction={sortField === "location" ? sortOrder : "asc"}
                                                onClick={() => setSort("location")}
                                            >
                                                Location
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Position</TableCell>
                                        <TableCell>Requested By</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === "created_at"}
                                                direction={sortField === "created_at" ? sortOrder : "asc"}
                                                onClick={() => setSort("created_at")}
                                            >
                                                Requested At
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {relocationRequests.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                    No relocation requests found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : relocationRequests.map((relocationRequest) => (
                                        <TableRow
                                            key={`${relocationRequest.id}`}
                                            sx={highlightedIds.has(relocationRequest.id) ? {
                                                animation: `${highlightFade} 1.5s ease-out forwards`,
                                            } : undefined}
                                        >
                                            <TableCell>{relocationRequest.product_stock.product.code}</TableCell>
                                            <TableCell>{relocationRequest.product_stock.product.name}</TableCell>
                                            <TableCell>{relocationRequest.location ? (
                                                <>{relocationRequest.location.name} ({relocationRequest.location.warehouse.name})</>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    Into stock
                                                </Typography>
                                            )}</TableCell>
                                            <TableCell>{relocationRequest.level != null ? `L${relocationRequest.level} C${relocationRequest.column}` : '-'}</TableCell>
                                            <TableCell>{relocationRequest.user.name}</TableCell>
                                            <TableCell>{relocationRequest.created_at.toLocaleDateString()} {relocationRequest.created_at.toLocaleTimeString()}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                    onClick={() => navigate(`/relocations/${relocationRequest.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card >
        </Box >
    );
}